/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import {
  ErrorRenderer,
  Loading,
  ResponsiveTable,
  useLocalization,
} from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { PaymentTerminalV2 } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import { useAuth } from "@app/vue/providers/Auth";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { EmptyView } from "../PaymentsSettings/EmptyView";
import { getTerminalProviderConfig } from "./providerConfig";
import { ProviderLogo } from "./ProviderLogo";
import {
  TerminalForm,
  type PayrexxIntegrationOption,
  type TerminalChange,
} from "./TerminalForm";
import { getTerminalIdentifier, getTerminalStatus, statusBadgeClass } from "./utils";

const NS = "settings.terminals";

export const TerminalsSettings = defineComponent({
  name: "TerminalsSettings",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();

    const terminals = ref<PaymentTerminalV2[]>([]);
    const payrexxIntegrations = ref<PayrexxIntegrationOption[]>([]);
    const loading = ref(true);
    const loadError = ref<Error | null>(null);
    const sheet = ref<{ mode: "create" | "edit"; terminal: PaymentTerminalV2 | null } | null>(
      null,
    );

    const staffNames = computed(
      () =>
        new Map(
          (auth.user.value?.colleagues || []).map((c) => [c.id, c.profile.name]),
        ),
    );

    const loadTerminals = async () => {
      try {
        loading.value = true;
        loadError.value = null;
        terminals.value = ((await trpc.terminals.list.query({})) as PaymentTerminalV2[]) || [];
      } catch (error) {
        loadError.value =
          error instanceof Error ? error : new Error("Failed to fetch terminals");
      } finally {
        loading.value = false;
      }
    };

    const loadPayrexxIntegrations = async () => {
      try {
        const res = (await trpc.paymentIntegrations.list.query({
          type: "payrexx",
        })) as { data?: { id: string; credentials?: { instance_name?: string } }[] };
        payrexxIntegrations.value = (res?.data || []).map((i) => ({
          id: i.id,
          label: i.credentials?.instance_name || i.id,
        }));
      } catch (error) {
        console.error("Failed to fetch Payrexx integrations:", error);
      }
    };

    onMounted(async () => {
      setTitle(t(`${NS}.page_title`));
      await Promise.all([loadTerminals(), loadPayrexxIntegrations()]);
    });

    const openCreate = () => {
      sheet.value = { mode: "create", terminal: null };
    };
    const openEdit = (terminal: PaymentTerminalV2) => {
      sheet.value = { mode: "edit", terminal };
    };
    const closeSheet = () => {
      sheet.value = null;
    };
    // Apply the saved row from the mutation result instead of refetching:
    // a read right after the write can come back stale (Hyperdrive caching).
    const onSaved = (change: TerminalChange) => {
      closeSheet();
      if (change.kind === "deleted") {
        terminals.value = terminals.value.filter((t) => t.id !== change.id);
        return;
      }
      const index = terminals.value.findIndex((t) => t.id === change.terminal.id);
      terminals.value =
        index === -1
          ? [...terminals.value, change.terminal]
          : terminals.value.map((t, i) => (i === index ? change.terminal : t));
    };

    // ── Cells ─────────────────────────────────────────────────────────────────

    const renderTerminalCell = (terminal: PaymentTerminalV2) => (
      <div class="flex min-w-0 items-center gap-3">
        <ProviderLogo provider={terminal.provider} size="sm" />
        <div class="min-w-0">
          <div class="truncate font-medium text-base-content">
            {terminal.name || t(`${NS}.terminal_fallback`)}
          </div>
          <div class="text-xs text-base-content/60">
            {getTerminalProviderConfig(terminal.provider).label}
          </div>
        </div>
      </div>
    );

    const renderStatus = (terminal: PaymentTerminalV2) => {
      const status = getTerminalStatus(terminal.status);
      return (
        <span class={`${statusBadgeClass[status.tone]} badge-sm`}>
          {t(status.labelKey)}
        </span>
      );
    };

    const renderIdentifier = (terminal: PaymentTerminalV2) => {
      const id = getTerminalIdentifier(terminal);
      return id ? (
        <span class="font-mono text-sm text-base-content/80">{id.value}</span>
      ) : (
        <span class="text-sm text-base-content/40">—</span>
      );
    };

    const staffSummary = (terminal: PaymentTerminalV2) => {
      const names = (terminal.staff_id || [])
        .map((id) => staffNames.value.get(Number(id)))
        .filter((n): n is string => !!n);
      if (names.length === 0) return null;
      return names.length <= 2
        ? names.join(", ")
        : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
    };

    const renderStaff = (terminal: PaymentTerminalV2) => {
      const summary = staffSummary(terminal);
      return summary ? (
        <span class="text-sm text-base-content/80">{summary}</span>
      ) : (
        <span class="text-sm text-base-content/40">{t(`${NS}.no_staff_assigned`)}</span>
      );
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-terminal",
        label: t(`${NS}.resource_name`),
        icon: "point_of_sale",
        mobilePriority: "hidden",
        render: renderTerminalCell,
      },
      {
        key: "d-status",
        label: t(`${NS}.status`),
        icon: "sensors",
        mobilePriority: "hidden",
        render: renderStatus,
      },
      {
        key: "d-identifier",
        label: t(`${NS}.serial_or_id`),
        icon: "fingerprint",
        mobilePriority: "hidden",
        render: renderIdentifier,
      },
      {
        key: "d-staff",
        label: t(`${NS}.assigned_staff`),
        icon: "group",
        mobilePriority: "hidden",
        render: renderStaff,
      },
      {
        key: "m-terminal",
        label: t(`${NS}.resource_name`),
        desktopHide: true,
        mobilePriority: "primary",
        render: renderTerminalCell,
      },
      {
        key: "m-status",
        label: t(`${NS}.status`),
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: renderStatus,
      },
      {
        key: "m-identifier",
        label: t(`${NS}.serial_or_id`),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (terminal: PaymentTerminalV2) =>
          getTerminalIdentifier(terminal)?.value ?? "—",
      },
      {
        key: "m-staff",
        label: t(`${NS}.assigned_staff`),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (terminal: PaymentTerminalV2) =>
          staffSummary(terminal) ?? t(`${NS}.no_staff_assigned`),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: () => (
          <span class="material-symbols-outlined text-base-content/40">
            chevron_right
          </span>
        ),
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="contactless"
          title={t(`${NS}.title`)}
          description={t(`${NS}.description`)}
        >
          {{
            actions: () => (
              <button
                disabled={loading.value}
                class="btn btn-primary btn-sm gap-2"
                onClick={openCreate}
              >
                <span class="material-symbols-outlined">add</span>
                {t(`${NS}.add_terminal`)}
              </button>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {loading.value && (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            )}

            {!loading.value && loadError.value && (
              <ErrorRenderer error={loadError.value} onRetry={loadTerminals} />
            )}

            {!loading.value && !loadError.value && terminals.value.length === 0 && (
              <EmptyView
                icon="contactless"
                title={t(`${NS}.empty_title`)}
                description={t(`${NS}.empty_description`)}
                actionLabel={t(`${NS}.add_terminal`)}
                onAction={openCreate}
              />
            )}

            {!loading.value && !loadError.value && terminals.value.length > 0 && (
              <ResponsiveTable
                columns={columns}
                rows={terminals.value}
                rowKey={(terminal: PaymentTerminalV2) => terminal.guid || terminal.id}
                onRowClick={openEdit}
              />
            )}
          </AppSubpageContent>
        </AppSubpage>

        {sheet.value && (
          <TerminalForm
            key={`${sheet.value.mode}-${sheet.value.terminal?.id ?? "new"}`}
            mode={sheet.value.mode}
            terminal={sheet.value.terminal}
            payrexxIntegrations={payrexxIntegrations.value}
            onSaved={onSaved}
            onClose={closeSheet}
          />
        )}
      </div>
    );
  },
});
